/**
 * 日報・管理者ビューを「どの部署の」ものとして開くか（PRODUCT_DEV-721 第3期 U6）
 * =====================================================
 *
 * 機能フラグ `managerRole` が ON のテナントは、日報と部の数字が**部署ごと**に分かれる。
 * 画面は「いまの部署」（PRODUCT_DEV-917 の `activeDeptId`）の日報を出し、API には `?deptId=` で渡す。
 * OFF のテナントのサーバは `deptId` を読まないので、画面はフラグを見ずに常に付けてよい。
 *
 * ## URL の `?deptId=`
 *
 * まとめメールと通知のリンクは部署を持つ（`withReportDept`）。開いたとき:
 * - **自分の所属の部署** → 「いまの部署」をその部署へ切り替え、URL から外す（メニューの切替と同じ扱い）
 * - **所属していない部署**（異動前の部署の日報を本人が読む） → 切り替えずに、その部署の日報を**読むだけ**で出す。
 *   他人の所属外の部署はサーバが 404 にする
 */

import { withReportDept } from "@shared-domain/unizo/notificationLink";

/**
 * URL に部署を足す（既にクエリがあれば `&` で）。部署が無ければそのまま。
 * クエリ名と組み立て方はメール・通知のリンクと同じ（正本は shared の `withReportDept`）。
 */
export const withDeptQuery = (url: string, deptId: string | null | undefined): string =>
  withReportDept(url, deptId);

export interface ReportDeptView {
  /** API に渡す部署。未所属なら null（渡さない）。 */
  deptId: string | null;
  /** いま所属していない部署（異動前）の日報を読んでいるか。 */
  isPastDept: boolean;
  /**
   * 読むだけか（書く・作る・コメントする操作は出さない。サーバも 404 で止める）。
   * 異動前の部署の日報と、機能フラグ ON のテナントで所属の無い人（部署を持たない過去の日報を読むだけ）
   */
  readOnly: boolean;
}

/**
 * URL の部署・いまの部署・自分の所属から、開く部署を決める。純関数。
 * 自分の所属の部署が URL にあれば、それを「いまの部署」として扱う（切替が反映される前の1描画でもずれない）。
 */
export const resolveReportDeptView = (input: {
  queryDeptId: string | null;
  activeDeptId: string | null;
  ownDeptIds: readonly string[];
  /** 機能フラグ `managerRole`。OFF は所属が無くても今までどおり書ける */
  roleSplit?: boolean;
}): ReportDeptView => {
  const { queryDeptId, activeDeptId, ownDeptIds } = input;
  const unassigned = Boolean(input.roleSplit) && ownDeptIds.length === 0;
  if (queryDeptId) {
    const isPastDept = !ownDeptIds.includes(queryDeptId);
    return { deptId: queryDeptId, isPastDept, readOnly: isPastDept || unassigned };
  }
  return { deptId: activeDeptId, isPastDept: false, readOnly: unassigned };
};

/**
 * URL の部署で「いまの部署」を切り替えるべきか。所属している部署で、いまの部署と違うときだけ。
 * 戻り値 `strip` は URL から `deptId` を外すか（所属している部署のときだけ外す。異動前の部署は URL に残して読む）。
 *
 * PRODUCT_DEV-917: **まだ一度も部署を選んでいない**（`hasSavedChoice: false`）ときは、いまの部署と同じでも
 * 保存する。「いまの部署」は保存が無いと先頭の所属に倒れているだけなので、保存しないと
 * (1) 初回の部署選択のシートがリンクから開いた直後に出る (2) 個人設定を読み終えたあと、サーバに残っていた
 * 別の部署へ表示が移り、リンクの部署で開かなかったことになる。
 */
export const deptLinkAction = (input: {
  queryDeptId: string | null;
  activeDeptId: string | null;
  ownDeptIds: readonly string[];
  hasSavedChoice?: boolean;
}): { switchTo: string | null; strip: boolean } => {
  const { queryDeptId, activeDeptId, ownDeptIds, hasSavedChoice = true } = input;
  if (!queryDeptId || !ownDeptIds.includes(queryDeptId)) return { switchTo: null, strip: false };
  return {
    switchTo: queryDeptId === activeDeptId && hasSavedChoice ? null : queryDeptId,
    strip: true,
  };
};
